<template>
  <ModalBase
    :show="show"
    :titel="pages[page].title"
    :button-confirm-text="page < pages.length - 1 ? 'Weiter' : 'Los geht’s'"
    :button-confirm-spinner="saving"
    button-cancel-text="Überspringen"
    modal-width="max-w-3xl"
    @on-confirm="next"
    @on-cancel="finish(false)"
  >
    <template #icon>
      <BIconStars class="h-5 w-5" />
    </template>

    <template #content>
      <!-- 1: welcome + invite colleagues -->
      <div v-if="page === 0" data-testid="onboarding-welcome-team">
        <p class="text-secondary-lighter text-sm">
          Schön, dass Sie da sind! In wenigen Schritten richten Sie SEC AUDITOR ein. Am besten laden Sie gleich Ihre Kollegen ein,
          damit Sie gemeinsam an Ihren Kunden arbeiten können.
        </p>
        <CollaborationSettings :is-client-team="false" />
      </div>

      <!-- 2: the three modes -->
      <div v-else-if="page === 1" class="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="onboarding-welcome-modes">
        <div v-for="mode in modes" :key="mode.title" class="rounded-md border border-secondary-light p-4">
          <component :is="mode.icon" class="h-6 w-6 text-lilac-light" />
          <p class="mt-2 font-bold text-common-white">
            {{ mode.title }}
          </p>
          <p class="mt-1 text-sm text-secondary-lighter">
            {{ mode.description }}
          </p>
        </div>
      </div>

      <!-- 3: next steps + changelog opt-in -->
      <div v-else data-testid="onboarding-welcome-finish">
        <p class="text-secondary-lighter text-sm">
          Unten rechts finden Sie ab jetzt die Checkliste „Erste Schritte“: NFR-Domäne anlegen, Entra ID verbinden und den Collector installieren.
          Jeder Schritt zeigt Ihnen auf Wunsch direkt, wo es weitergeht.
        </p>

        <label
          v-if="canSubscribeChangelog"
          class="mt-6 flex items-start gap-4 cursor-pointer rounded-md border-2 p-4 transition-colors"
          :class="subscribeChangelog ? 'border-lilac bg-lilac/10' : 'border-lilac/50 bg-secondary hover:border-lilac'"
        >
          <input
            v-model="subscribeChangelog"
            type="checkbox"
            class="checkbox-standard mt-1 h-5 w-5"
            data-testid="onboarding-changelog-opt-in"
          >
          <span>
            <span class="flex items-center gap-2 font-bold text-common-white">
              <BIconEnvelopePaper class="h-5 w-5 text-lilac-light" />
              Changelog abonnieren
            </span>
            <span class="mt-1 block text-sm text-secondary-lighter">
              Ja, ich möchte per E-Mail über neue Funktionen und Änderungen informiert werden.
            </span>
            <span class="mt-2 block text-xs text-secondary-light">
              Abmeldung jederzeit über den Link in jeder E-Mail oder in den Account-Einstellungen.
            </span>
          </span>
        </label>
      </div>
    </template>

    <template #actionsLeft>
      <button
        v-if="page > 0"
        type="button"
        class="btn-standard-no-color border-secondary-light bg-secondary text-common-white px-4 py-2"
        @click="page--"
      >
        Zurück
      </button>
    </template>
  </ModalBase>
</template>

<script setup lang="ts">
import { BIconActivity, BIconCardChecklist, BIconClipboardData, BIconEnvelopePaper, BIconStars } from 'bootstrap-icons-vue'
import ModalBase from '~/components/shared/modals/ModalBase.vue'
import CollaborationSettings from '~/components/shared/CollaborationSettings.vue'

defineProps({
  show: {
    type: Boolean,
    required: true
  }
})

const { closeWelcome } = useOnboarding()
const { currentEnv } = useCurrentUserSession()

const page = ref(0)
const saving = ref(false)
// opt-in: never pre-checked
const subscribeChangelog = ref(false)
const canSubscribeChangelog = !currentEnv.isSelfhosted && !currentEnv.isDemoEnv

const pages = [
  { title: 'Willkommen bei SEC AUDITOR' },
  { title: 'Drei Modi für Ihre Kunden' },
  { title: 'Fast geschafft' }
]

const modes = [
  {
    title: 'Monitoring',
    icon: BIconActivity,
    description: 'Dauerhafte Überwachung von AD und Entra ID mit täglichen Schwachstellen-Analysen und Angriffserkennung in Echtzeit.'
  },
  {
    title: 'Audit',
    icon: BIconClipboardData,
    description: 'Einmalige, tiefgehende Analyse einer Umgebung inklusive Report, z. B. für ein Projekt beim Kunden.'
  },
  {
    title: 'Quickcheck',
    icon: BIconCardChecklist,
    description: 'Kostenlose Kurzanalyse für Interessenten, auch als Whitelabel-Formular auf Ihrer Webseite.'
  }
]

async function next (): Promise<void> {
  if (page.value < pages.length - 1) {
    page.value++
    return
  }
  await finish(subscribeChangelog.value)
}

async function finish (subscribe: boolean): Promise<void> {
  saving.value = true
  try {
    await closeWelcome(subscribe)
  } finally {
    saving.value = false
  }
}
</script>
